import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Ticket, Search, CheckCircle, XCircle, Clock, Gift } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH')}`;

const ClassPasses = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', is_gift: false, gift_recipient_name: '', gift_recipient_email: '', gift_message: '' });
  const [checkCode, setCheckCode] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [giftPickerOpen, setGiftPickerOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { fetchProducts(); }, []);

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setCheckCode(code.toUpperCase());
      lookupPass(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/pass-products`);
      setProducts(res.data);
    } catch (error) {
      console.error('Error fetching pass products:', error);
    }
  };

  const openBuy = (product, gift = false) => {
    setGiftPickerOpen(false);
    setSelected(product);
    setForm({ customer_name: '', customer_email: '', customer_phone: '', is_gift: gift, gift_recipient_name: '', gift_recipient_email: '', gift_message: '' });
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.is_gift && (!form.gift_recipient_name || !form.gift_recipient_email)) {
      toast.error("Please enter the recipient's name and email");
      return;
    }
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        booking_type: 'pass',
        pass_product_id: selected.id,
        booking_date: new Date().toISOString().split('T')[0],
      };
      if (form.is_gift) {
        payload.gift_recipient_name = form.gift_recipient_name;
        payload.gift_recipient_email = form.gift_recipient_email;
        payload.gift_message = form.gift_message;
      }
      const res = await axios.post(`${API}/bookings`, payload);
      toast.success('Pass reserved! Redirecting to payment...');
      setTimeout(() => navigate(`/payment/${res.data.id}`), 1000);
    } catch (error) {
      console.error('Error buying pass:', error);
      toast.error(error.response?.data?.detail || 'Failed to reserve pass');
    }
  };

  const lookupPass = async (codeArg) => {
    const code = (codeArg ?? checkCode).trim().toUpperCase();
    if (!code) { toast.error('Enter your class pass code'); return; }
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await axios.get(`${API}/passes/check`, { params: { code } });
      setCheckResult(res.data);
    } catch (error) {
      setCheckResult({ error: error.response?.data?.detail || 'Class pass not found.' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-testid="class-passes-header">
          <div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
              CLASS PASSES
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600">
              Save more with a multi-class pass. 1 credit = 1 class — use your code as a payment option at checkout.
            </p>
          </div>
          {products.length > 0 && (
            <button
              onClick={() => setGiftPickerOpen(true)}
              className="self-start md:self-auto shrink-0 inline-flex items-center gap-2 bg-black text-white hover:bg-zinc-800 rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95"
              data-testid="gift-a-pass-btn"
            >
              <Gift size={20} /> GIFT A CLASS PASS
            </button>
          )}
        </div>

        {products.length === 0 ? (
          <p className="text-zinc-500 mb-16" data-testid="class-passes-empty">No class passes available right now. Check back soon!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {products.map((p) => (
              <div key={p.id} className="relative bg-white border-2 border-black p-8 flex flex-col" data-testid={`pass-product-${p.id}`}>
                {p.savings_percent > 0 && (
                  <span
                    className="absolute -top-3 right-6 bg-[var(--accent)] text-black text-sm font-bold px-3 py-1 rounded-full shadow-md"
                    data-testid={`pass-savings-${p.id}`}
                  >
                    SAVE {p.savings_percent}%
                  </span>
                )}
                <Ticket size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--heading-font)' }}>{p.name}</h3>
                <p className="text-sm text-zinc-500 mb-4">{p.credits} class{p.credits === 1 ? '' : 'es'}{p.expiry_days ? ` • valid ${p.expiry_days} days after 1st class` : ' • no expiry'}</p>
                {p.description && <p className="text-sm text-zinc-600 mb-4">{p.description}</p>}
                <div className="text-3xl font-bold mb-1 mt-auto" style={{ fontFamily: 'var(--heading-font)' }} data-testid={`pass-price-${p.id}`}>{peso(p.price)}</div>
                <p className="text-xs text-zinc-400 mb-1">{peso(p.per_class_price ?? (p.credits ? p.price / p.credits : 0))} per class</p>
                {p.savings_percent > 0 && (
                  <p className="text-sm font-bold text-green-700 mb-5" data-testid={`pass-savings-line-${p.id}`}>
                    You save {p.savings_percent}% vs {peso(p.regular_class_price)}/class
                  </p>
                )}
                {!(p.savings_percent > 0) && <div className="mb-5" />}
                <button
                  onClick={() => openBuy(p)}
                  className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95"
                  data-testid={`buy-pass-btn-${p.id}`}
                >
                  BUY THIS PASS
                </button>
                <button
                  onClick={() => openBuy(p, true)}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 text-sm font-medium text-zinc-600 hover:text-black transition-colors"
                  data-testid={`gift-pass-btn-${p.id}`}
                >
                  <Gift size={16} /> Gift this pass
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Check balance */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 max-w-2xl" data-testid="check-balance-section">
          <h2 className="text-2xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
            <Search size={22} /> Check Your Remaining Credits
          </h2>
          <p className="text-sm text-zinc-600 mb-4">Already have a pass? Enter your code to see your balance and expiry.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={checkCode}
              onChange={(e) => setCheckCode(e.target.value.toUpperCase())}
              placeholder="PASS-XXXX-XXXX"
              className="font-mono tracking-wider"
              data-testid="check-code-input"
            />
            <button
              onClick={() => lookupPass()}
              disabled={checking}
              className="bg-black text-white rounded-full px-6 py-3 font-bold hover:bg-zinc-800 transition-colors disabled:opacity-50 shrink-0"
              data-testid="check-code-btn"
            >
              {checking ? 'CHECKING...' : 'CHECK BALANCE'}
            </button>
          </div>

          {checkResult && (
            <div className="mt-6" data-testid="check-result">
              {checkResult.error ? (
                <div className="flex items-center gap-2 text-red-600" data-testid="check-error">
                  <XCircle size={18} /> {checkResult.error}
                </div>
              ) : (
                <div className="bg-white border-2 border-black rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-400">{checkResult.product_name}</p>
                      <p className="font-mono font-bold text-lg">{checkResult.code}</p>
                    </div>
                    {checkResult.status === 'active' ? (
                      <span className="flex items-center gap-1 text-green-700 text-sm font-medium"><CheckCircle size={16} /> Active</span>
                    ) : checkResult.status === 'expired' ? (
                      <span className="flex items-center gap-1 text-red-600 text-sm font-medium"><Clock size={16} /> Expired</span>
                    ) : (
                      <span className="flex items-center gap-1 text-zinc-500 text-sm font-medium"><XCircle size={16} /> Used Up</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="check-credits-remaining">{checkResult.credits_remaining}</span>
                    <span className="text-zinc-500">of {checkResult.credits_total} credits remaining</span>
                  </div>
                  <p className="text-sm text-zinc-500 mt-2">{checkResult.expiry_note || (checkResult.expiry_date ? `Valid until: ${checkResult.expiry_date}` : 'No expiry')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Buy Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="buy-pass-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {form.is_gift ? `Gift ${selected?.name}` : `Buy ${selected?.name}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBuy} className="space-y-4">
            <div>
              <Label htmlFor="pass_name">Full Name *</Label>
              <Input id="pass_name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="pass-name-input" />
            </div>
            <div>
              <Label htmlFor="pass_email">Email *</Label>
              <Input id="pass_email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} required data-testid="pass-email-input" />
              <p className="text-xs text-zinc-500 mt-1">Your pass code will be emailed here once payment is verified.</p>
            </div>
            <div>
              <Label htmlFor="pass_phone">Phone Number *</Label>
              <Input id="pass_phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} required data-testid="pass-phone-input" />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer border border-zinc-200 rounded p-3" data-testid="pass-gift-toggle">
              <input type="checkbox" checked={form.is_gift} onChange={(e) => setForm({ ...form, is_gift: e.target.checked })} className="w-4 h-4 accent-black" data-testid="pass-gift-checkbox" />
              🎁 This is a gift — send the code to someone else
            </label>

            {form.is_gift && (
              <div className="space-y-3 border-l-2 border-[var(--accent)] pl-3" data-testid="pass-gift-fields">
                <div>
                  <Label htmlFor="gift_name">Recipient's Name *</Label>
                  <Input id="gift_name" value={form.gift_recipient_name} onChange={(e) => setForm({ ...form, gift_recipient_name: e.target.value })} required={form.is_gift} data-testid="gift-name-input" />
                </div>
                <div>
                  <Label htmlFor="gift_email">Recipient's Email *</Label>
                  <Input id="gift_email" type="email" value={form.gift_recipient_email} onChange={(e) => setForm({ ...form, gift_recipient_email: e.target.value })} required={form.is_gift} data-testid="gift-email-input" />
                  <p className="text-xs text-zinc-500 mt-1">The pass code will be emailed directly to them once your payment is verified.</p>
                </div>
                <div>
                  <Label htmlFor="gift_message">Gift Message (optional)</Label>
                  <Input id="gift_message" value={form.gift_message} onChange={(e) => setForm({ ...form, gift_message: e.target.value })} placeholder="Happy birthday! 💃" data-testid="gift-message-input" />
                </div>
              </div>
            )}

            <div className="bg-zinc-50 p-4 rounded flex justify-between items-center">
              <span className="font-medium">{selected?.credits} credits</span>
              <span className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="pass-total-amount">{peso(selected?.price)}</span>
            </div>
            <div className="pt-2 space-y-2">
              <button type="submit" className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95" data-testid="pass-confirm-btn">
                PROCEED TO PAYMENT
              </button>
              <button type="button" onClick={() => setSelected(null)} className="w-full border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors" data-testid="pass-cancel-btn">
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gift Picker Dialog */}
      <Dialog open={giftPickerOpen} onOpenChange={setGiftPickerOpen}>
        <DialogContent className="max-w-md" data-testid="gift-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
              <Gift size={24} /> Gift a Class Pass
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600 mb-2">Choose a pass to gift. We'll email the code straight to your recipient once your payment is verified.</p>
          <div className="space-y-3">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => openBuy(p, true)}
                className="w-full text-left border-2 border-zinc-200 hover:border-black rounded-xl p-4 transition-colors flex items-center justify-between"
                data-testid={`gift-pick-${p.id}`}
              >
                <div>
                  <p className="font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{p.name}</p>
                  <p className="text-xs text-zinc-500">{p.credits} class{p.credits === 1 ? '' : 'es'}{p.savings_percent > 0 ? ` • save ${p.savings_percent}%` : ''}</p>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{peso(p.price)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassPasses;
